import NodePanelTabs from './NodePanelTabs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getThemeDiscussionPalette, getThemeNodeFontFamily } from '../../config/themes';

const PANEL_WIDTH = 360;
const PANEL_HEIGHT = 520;
const MIN_PANEL_WIDTH = 300;
const MIN_PANEL_HEIGHT = 320;
const PANEL_GAP = 16;
const VIEWPORT_MARGIN = 16;
const MOBILE_BREAKPOINT = 720;
const MAX_MESSAGE_CHARACTERS = 2000;

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

export function getDiscussionPanelLayout(anchorRect, viewport) {
  const width = viewport.width || 0;
  const height = viewport.height || 0;
  if (width < MOBILE_BREAKPOINT) {
    return {
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '78vh',
      maxHeight: '78vh',
      borderRadius: '18px 18px 0 0',
    };
  }

  const anchor = anchorRect || {
    left: width - PANEL_WIDTH - VIEWPORT_MARGIN,
    right: width - PANEL_WIDTH - VIEWPORT_MARGIN,
    top: VIEWPORT_MARGIN,
    bottom: VIEWPORT_MARGIN,
  };
  const fitsRight = anchor.right + PANEL_GAP + PANEL_WIDTH <= width - VIEWPORT_MARGIN;
  const left = fitsRight
    ? anchor.right + PANEL_GAP
    : anchor.left - PANEL_GAP - PANEL_WIDTH;
  const availableHeight = Math.max(0, height - (VIEWPORT_MARGIN * 2));
  const panelHeight = Math.min(PANEL_HEIGHT, availableHeight);
  return {
    position: 'fixed',
    left: clamp(left, VIEWPORT_MARGIN, width - PANEL_WIDTH - VIEWPORT_MARGIN),
    top: clamp(anchor.top, VIEWPORT_MARGIN, height - panelHeight - VIEWPORT_MARGIN),
    width: PANEL_WIDTH,
    height: panelHeight,
    minWidth: Math.min(MIN_PANEL_WIDTH, width - (VIEWPORT_MARGIN * 2)),
    minHeight: Math.min(MIN_PANEL_HEIGHT, availableHeight),
    maxWidth: Math.max(0, width - (VIEWPORT_MARGIN * 2)),
    maxHeight: availableHeight,
    borderRadius: 18,
  };
}

function initials(label) {
  const parts = String(label || 'Mindmappi collaborator')
    .trim()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  if (parts.length === 0) return 'M';
  return `${parts[0][0] || ''}${parts[1]?.[0] || ''}`.toUpperCase();
}

function authorColor(userId) {
  let hash = 0;
  for (const character of String(userId || 'former-collaborator')) {
    hash = ((hash << 5) - hash) + character.charCodeAt(0);
    hash |= 0;
  }
  return `hsl(${Math.abs(hash) % 360} 62% 38%)`;
}

function formatTimestamp(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function displayError(error) {
  if (error?.code === 'node_discussion_rate_limit_reached') {
    const wait = error.retryAfterSeconds
      ? ` Try again in about ${error.retryAfterSeconds} seconds.`
      : ' Please try again shortly.';
    return `You’ve sent messages a little too quickly.${wait}`;
  }
  return error?.message || 'This discussion could not be loaded.';
}

function NodeDiscussionPanel({
  node,
  anchorRect,
  isDarkMode,
  theme,
  currentUserId,
  thread,
  isLoading,
  isLoadingOlder,
  isSending,
  error,
  onClose,
  onLoadOlder,
  onSend,
  onRefresh,
  onInfo,
  initialDraft = '',
  onDraftChange,
}) {
  const [draft, setLocalDraft] = useState(initialDraft);
  const setDraft = (value) => {
    setLocalDraft(value);
    onDraftChange?.(value);
  };
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === 'undefined' ? 1024 : window.innerWidth,
    height: typeof window === 'undefined' ? 768 : window.innerHeight,
  }));
  const [manualPosition, setManualPosition] = useState(null);
  const [manualSize, setManualSize] = useState(null);
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const composerRef = useRef(null);
  const messagesRef = useRef(null);
  const messageCount = thread?.messages?.length || 0;
  const anchoredLayout = useMemo(
    () => getDiscussionPanelLayout(anchorRect, viewport),
    [anchorRect, viewport]
  );
  const isMobile = viewport.width < MOBILE_BREAKPOINT;
  const positionedLayout = manualPosition && !isMobile
    ? {
        ...anchoredLayout,
        left: manualPosition.left,
        top: manualPosition.top,
      }
    : anchoredLayout;
  const maximumPanelWidth = isMobile
    ? null
    : Math.max(
      anchoredLayout.minWidth,
      viewport.width - positionedLayout.left - VIEWPORT_MARGIN
    );
  const maximumPanelHeight = isMobile
    ? null
    : Math.max(
      anchoredLayout.minHeight,
      viewport.height - positionedLayout.top - VIEWPORT_MARGIN
    );
  const layout = isMobile
    ? positionedLayout
    : {
        ...positionedLayout,
        width: clamp(
          manualSize?.width ?? positionedLayout.width,
          anchoredLayout.minWidth,
          maximumPanelWidth
        ),
        height: clamp(
          manualSize?.height ?? positionedLayout.height,
          anchoredLayout.minHeight,
          maximumPanelHeight
        ),
      };
  const resizeBounds = isMobile
    ? {}
    : {
        maxWidth: maximumPanelWidth,
        maxHeight: maximumPanelHeight,
      };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setViewport({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);
    composerRef.current?.focus({ preventScroll: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    setManualPosition(null);
    setManualSize(null);
  }, [node?.id]);

  useEffect(() => {
    if (!manualPosition || isMobile) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nextPosition = {
      left: clamp(
        manualPosition.left,
        VIEWPORT_MARGIN,
        viewport.width - rect.width - VIEWPORT_MARGIN
      ),
      top: clamp(
        manualPosition.top,
        VIEWPORT_MARGIN,
        viewport.height - rect.height - VIEWPORT_MARGIN
      ),
    };
    if (
      nextPosition.left !== manualPosition.left
      || nextPosition.top !== manualPosition.top
    ) {
      setManualPosition(nextPosition);
    }
  }, [isMobile, manualPosition, viewport]);

  useEffect(() => {
    const element = messagesRef.current;
    if (element && !isLoadingOlder) {
      element.scrollTop = element.scrollHeight;
    }
  }, [isLoadingOlder, messageCount]);

  const palette = getThemeDiscussionPalette(theme, isDarkMode);

  const submit = async () => {
    if (!draft.trim() || draft.length > MAX_MESSAGE_CHARACTERS || isSending) return;
    const sent = await onSend(draft);
    if (sent) {
      setDraft('');
      composerRef.current?.focus({ preventScroll: true });
    }
  };

  const startDrag = (event) => {
    if (isMobile || event.button !== 0 || event.target.closest('button')) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      pointerId: event.pointerId,
      pointerX: event.clientX,
      pointerY: event.clientY,
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
    setManualPosition({ left: rect.left, top: rect.top });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const moveDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setManualPosition({
      left: clamp(
        drag.left + event.clientX - drag.pointerX,
        VIEWPORT_MARGIN,
        viewport.width - drag.width - VIEWPORT_MARGIN
      ),
      top: clamp(
        drag.top + event.clientY - drag.pointerY,
        VIEWPORT_MARGIN,
        viewport.height - drag.height - VIEWPORT_MARGIN
      ),
    });
  };

  const endDrag = (event) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const resizePanel = (width, height) => {
    if (isMobile) return;
    setManualSize({
      width: clamp(
        width,
        anchoredLayout.minWidth,
        maximumPanelWidth
      ),
      height: clamp(
        height,
        anchoredLayout.minHeight,
        maximumPanelHeight
      ),
    });
  };

  const startPanelResize = (event) => {
    if (isMobile || event.button !== 0) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    resizeRef.current = {
      pointerId: event.pointerId,
      pointerX: event.clientX,
      pointerY: event.clientY,
      width: rect.width,
      height: rect.height,
    };
    setManualSize({ width: rect.width, height: rect.height });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  };

  const movePanelResize = (event) => {
    const resize = resizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;
    resizePanel(
      resize.width + event.clientX - resize.pointerX,
      resize.height + event.clientY - resize.pointerY
    );
    event.preventDefault();
    event.stopPropagation();
  };

  const endPanelResize = (event) => {
    if (resizeRef.current?.pointerId !== event.pointerId) return;
    resizeRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    event.stopPropagation();
  };

  const resizePanelWithKeyboard = (event) => {
    if (isMobile) return;
    const horizontal = event.key === 'ArrowRight'
      ? 20
      : (event.key === 'ArrowLeft' ? -20 : 0);
    const vertical = event.key === 'ArrowDown'
      ? 20
      : (event.key === 'ArrowUp' ? -20 : 0);
    if (!horizontal && !vertical) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    event.preventDefault();
    event.stopPropagation();
    resizePanel(rect.width + horizontal, rect.height + vertical);
  };

  return (
    <section
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby="node-discussion-title"
      className="node-discussion-panel"
      data-export-ignore="true"
      data-viewport-occlusion="true"
      style={{
        ...layout,
        ...resizeBounds,
        zIndex: 180,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
        background: palette.panel,
        color: palette.text,
        border: `1px solid ${palette.border}`,
        boxShadow: '0 22px 60px rgba(15, 23, 42, 0.34)',
        fontFamily: getThemeNodeFontFamily(theme),
        resize: 'none',
      }}
      onPointerDown={(event) => event.stopPropagation()}
      onPointerMove={(event) => event.stopPropagation()}
      onPointerUp={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onMouseMove={(event) => event.stopPropagation()}
      onMouseUp={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 14px',
          borderBottom: `1px solid ${palette.border}`,
          cursor: isMobile ? 'default' : 'grab',
          touchAction: 'none',
          userSelect: 'none',
        }}
        title={isMobile ? undefined : 'Drag to move discussion'}
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <NodePanelTabs activeTab="discuss" palette={palette} onInfo={onInfo} onDiscuss={() => {}} />
          <h2
            id="node-discussion-title"
            style={{
              margin: '2px 0 0',
              opacity: 0.72,
              fontWeight: 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 12,
            }}
          >
            {(node?.text || '').trim() || 'Untitled idea'}
          </h2>
        </div>
        <button
          type="button"
          aria-label="Close idea discussion"
          onClick={onClose}
          style={{
            padding: 4,
            flex: '0 0 auto',
            border: 'none',
            background: 'transparent',
            color: palette.text,
            cursor: 'pointer',
            fontSize: 22,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </header>

      <div
        ref={messagesRef}
        className="node-discussion-messages"
        aria-live="polite"
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          padding: 12,
        }}
      >
        {thread?.page?.hasMore && (
          <button
            type="button"
            onClick={onLoadOlder}
            disabled={isLoadingOlder}
            style={{
              display: 'block',
              margin: '0 auto 14px',
              border: `1px solid ${palette.border}`,
              borderRadius: 999,
              padding: '7px 12px',
              background: palette.raised,
              color: palette.text,
              cursor: isLoadingOlder ? 'wait' : 'pointer',
            }}
          >
            {isLoadingOlder ? 'Loading…' : 'Load earlier messages'}
          </button>
        )}

        {isLoading && <p role="status">Opening discussion…</p>}

        {!isLoading && !error && messageCount === 0 && (
          <div style={{ padding: '28px 12px', textAlign: 'center' }}>
            <div aria-hidden="true" style={{ fontSize: 28 }}>💬</div>
            <p style={{ margin: '10px 0 4px', fontWeight: 700 }}>
              Start the conversation
            </p>
            <p style={{ margin: 0, color: palette.muted, fontSize: 13, lineHeight: 1.5 }}>
              Keep thoughts and decisions close to this idea.
            </p>
          </div>
        )}

        {error && (
          <div role="alert" style={{ color: '#ef4444', lineHeight: 1.45 }}>
            <p>{displayError(error)}</p>
            <button type="button" onClick={onRefresh}>Try again</button>
          </div>
        )}

        {(thread?.messages || []).map((message) => {
          const label = message.author?.displayName || 'Mindmappi collaborator';
          const isCurrentUser = message.author?.userId === currentUserId;
          return (
            <article
              key={message.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '34px minmax(0, 1fr)',
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: authorColor(message.author?.userId),
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 800,
                }}
              >
                {initials(label)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13 }}>
                    {isCurrentUser ? 'You' : label}
                  </strong>
                  <time
                    dateTime={message.createdAt}
                    style={{ color: palette.muted, fontSize: 11 }}
                  >
                    {formatTimestamp(message.createdAt)}
                  </time>
                </div>
                <p
                  style={{
                    margin: '4px 0 0',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    lineHeight: 1.5,
                    fontSize: 14,
                  }}
                >
                  {message.body}
                </p>
              </div>
            </article>
          );
        })}
      </div>

      <footer
        style={{
          padding: 8,
          borderTop: `1px solid ${palette.border}`,
          background: palette.raised,
        }}
      >
        <label htmlFor="node-discussion-composer" style={{ position: 'absolute', left: -10000 }}>
          Add to this discussion
        </label>
        <textarea
          id="node-discussion-composer"
          ref={composerRef}
          value={draft}
          maxLength={MAX_MESSAGE_CHARACTERS}
          rows={2}
          placeholder="Add to this discussion…"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (
              event.key === 'Enter'
              && !event.shiftKey
              && !event.nativeEvent?.isComposing
            ) {
              event.preventDefault();
              void submit();
            }
          }}
          style={{
            width: '100%',
            minHeight: 56,
            boxSizing: 'border-box',
            border: `1px solid ${palette.border}`,
            borderRadius: 12,
            padding: 8,
            background: palette.input,
            color: palette.text,
            font: 'inherit',
            resize: 'none',
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginTop: 8,
          }}
        >
          <span style={{ color: palette.muted, fontSize: 11 }}>
            {draft.length}/{MAX_MESSAGE_CHARACTERS} · Shift + Enter for a new line
          </span>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!draft.trim() || isSending}
            style={{
              border: 'none',
              borderRadius: 999,
              padding: '9px 15px',
              background: palette.accent,
              color: palette.accentText,
              cursor: !draft.trim() || isSending ? 'not-allowed' : 'pointer',
              opacity: !draft.trim() || isSending ? 0.55 : 1,
              fontWeight: 750,
            }}
          >
            {isSending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </footer>
      {!isMobile && (
        <div
          role="separator"
          aria-label="Resize idea discussion"
          tabIndex="0"
          title="Drag to resize discussion"
          onPointerDown={startPanelResize}
          onPointerMove={movePanelResize}
          onPointerUp={endPanelResize}
          onPointerCancel={endPanelResize}
          onKeyDown={resizePanelWithKeyboard}
          style={{
            position: 'absolute',
            right: 3,
            bottom: 3,
            zIndex: 2,
            width: 16,
            height: 16,
            boxSizing: 'border-box',
            borderRight: `3px solid ${palette.muted}`,
            borderBottom: `3px solid ${palette.muted}`,
            borderRadius: '0 0 5px 0',
            cursor: 'nwse-resize',
            touchAction: 'none',
          }}
        />
      )}
    </section>
  );
}

export default NodeDiscussionPanel;
